import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  creatorQuestionsService,
  CreatorQuestionsByCourse,
  CreatorQaStats,
  CreatorQuestion,
} from '../services/creatorQuestionsService';
import './CreatorLearnerQuestionsPage.css';

type StatusFilter = 'pending' | 'answered' | 'dismissed';

const CreatorLearnerQuestionsPage: React.FC = () => {
  const { t } = useTranslation('creator');

  const [stats, setStats] = useState<CreatorQaStats | null>(null);
  const [data, setData] = useState<CreatorQuestionsByCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');

  // Per-segment composer state — keyed by `${courseId}::${segmentId}` so that
  // multiple segments can be drafted in parallel without leaking text between them.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [applyToOthers, setApplyToOthers] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [dismissing, setDismissing] = useState<Record<string, boolean>>({});

  const segKey = (courseId: string, segmentId: string) => `${courseId}::${segmentId}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        creatorQuestionsService.stats(),
        creatorQuestionsService.listForCreator({ status: statusFilter }),
      ]);
      setStats(statsRes);
      setData(listRes);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleAnswer = async (
    courseId: string,
    segmentId: string,
    primary: CreatorQuestion,
    others: CreatorQuestion[],
  ) => {
    const key = segKey(courseId, segmentId);
    const text = (drafts[key] ?? '').trim();
    if (!text) return;
    const applyAll = (applyToOthers[key] ?? true) && others.length > 0;

    setSubmitting((s) => ({ ...s, [key]: true }));
    try {
      await creatorQuestionsService.answer(primary.id, {
        answerText: text,
        applyToQuestionIds: applyAll ? others.map((q) => q.id) : undefined,
      });
      setDrafts((d) => ({ ...d, [key]: '' }));
      await refresh();
    } finally {
      setSubmitting((s) => ({ ...s, [key]: false }));
    }
  };

  const handleDismiss = async (questionId: string) => {
    setDismissing((d) => ({ ...d, [questionId]: true }));
    try {
      await creatorQuestionsService.dismiss(questionId, {});
      await refresh();
    } finally {
      setDismissing((d) => ({ ...d, [questionId]: false }));
    }
  };

  return (
    <div className="creator-questions-page">
      <header className="creator-questions-page__header">
        <h1>{t('learnerQuestions.title')}</h1>
        <p className="creator-questions-page__subtitle">{t('learnerQuestions.subtitle')}</p>
      </header>

      {stats && (
        <section className="creator-questions-page__stats">
          <div className="cq-stat-card">
            <div className="cq-stat-card__label">{t('learnerQuestions.statsPending')}</div>
            <div className="cq-stat-card__value">{stats.pendingCount}</div>
          </div>
          <div className="cq-stat-card">
            <div className="cq-stat-card__label">{t('learnerQuestions.statsAnswered')}</div>
            <div className="cq-stat-card__value">{stats.answeredCount}</div>
          </div>
          <div className="cq-stat-card">
            <div className="cq-stat-card__label">{t('learnerQuestions.statsAvgResponse')}</div>
            <div className="cq-stat-card__value">
              {stats.avgResponseTimeMinutes !== null
                ? t('learnerQuestions.minutes', { count: stats.avgResponseTimeMinutes })
                : '—'}
            </div>
          </div>
        </section>
      )}

      <nav className="creator-questions-page__filters" role="tablist">
        {(['pending', 'answered', 'dismissed'] as const).map((s) => (
          <button
            key={s}
            role="tab"
            aria-selected={statusFilter === s}
            className={`cq-filter${statusFilter === s ? ' cq-filter--active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {t(`learnerQuestions.filter.${s}`)}
          </button>
        ))}
      </nav>

      {loading && <div className="creator-questions-page__loading">{t('learnerQuestions.loading')}</div>}

      {!loading && data.length === 0 && (
        <div className="creator-questions-page__empty">
          {t(`learnerQuestions.empty.${statusFilter}`)}
        </div>
      )}

      {data.map((course) => (
        <section key={course.courseId} className="cq-course">
          <header className="cq-course__header">
            <h2>{course.courseTitle}</h2>
            {course.pendingCount > 0 && (
              <span className="cq-course__badge">
                {t('learnerQuestions.pendingBadge', { count: course.pendingCount })}
              </span>
            )}
          </header>
          {course.segments.map((seg) => {
            const key = segKey(course.courseId, seg.segmentId);
            const [primary, ...others] = seg.questions;
            const isPending = statusFilter === 'pending';
            return (
              <article key={seg.segmentId} className="cq-segment">
                <div className="cq-segment__sentence">
                  <span className="cq-segment__sentence-label">
                    {t('learnerQuestions.sentenceLabel')}
                  </span>
                  <span className="cq-segment__sentence-text">{seg.sentenceText}</span>
                  {seg.translation && (
                    <span className="cq-segment__sentence-translation">{seg.translation}</span>
                  )}
                </div>

                <div className="cq-segment__questions">
                  {seg.questions.map((q, idx) => (
                    <div key={q.id} className="cq-question">
                      <div className="cq-question__meta">
                        <span className="cq-question__author">
                          {q.learnerDisplayName ?? t('learnerQuestions.anonymousLearner')}
                        </span>
                        <span className="cq-question__date">
                          {new Date(q.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="cq-question__text">{q.originalQuestion}</div>
                      {q.aiAnswer && (
                        <details className="cq-question__ai">
                          <summary>{t('learnerQuestions.showAiAnswer')}</summary>
                          <div className="cq-question__ai-body">{q.aiAnswer}</div>
                        </details>
                      )}
                      {isPending && idx > 0 && (
                        <button
                          className="cq-question__dismiss"
                          onClick={() => handleDismiss(q.id)}
                          disabled={!!dismissing[q.id]}
                        >
                          {dismissing[q.id]
                            ? t('learnerQuestions.dismissing')
                            : t('learnerQuestions.dismiss')}
                        </button>
                      )}
                      {!isPending && q.answerText && (
                        <div className="cq-question__creator-answer">
                          <span className="cq-question__creator-answer-label">
                            {t('learnerQuestions.yourAnswer')}
                          </span>
                          <div>{q.answerText}</div>
                        </div>
                      )}
                      {!isPending && q.dismissReason && (
                        <div className="cq-question__dismiss-reason">
                          {t('learnerQuestions.dismissReason', { reason: q.dismissReason })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {isPending && primary && (
                  <div className="cq-segment__composer">
                    <textarea
                      className="cq-segment__textarea"
                      value={drafts[key] ?? ''}
                      onChange={(e) =>
                        setDrafts((d) => ({ ...d, [key]: e.target.value }))
                      }
                      rows={4}
                      placeholder={t('learnerQuestions.composerPlaceholder')}
                    />
                    {others.length > 0 && (
                      <label className="cq-segment__apply-all">
                        <input
                          type="checkbox"
                          checked={applyToOthers[key] ?? true}
                          onChange={(e) =>
                            setApplyToOthers((a) => ({ ...a, [key]: e.target.checked }))
                          }
                        />
                        <span>
                          {t('learnerQuestions.applyToOthers', { count: others.length })}
                        </span>
                      </label>
                    )}
                    <div className="cq-segment__composer-actions">
                      <button
                        className="cq-segment__primary-btn"
                        onClick={() => handleAnswer(course.courseId, seg.segmentId, primary, others)}
                        disabled={!drafts[key]?.trim() || !!submitting[key]}
                      >
                        {submitting[key]
                          ? t('learnerQuestions.submitting')
                          : t('learnerQuestions.submitAnswer')}
                      </button>
                      <button
                        className="cq-segment__secondary-btn"
                        onClick={() => handleDismiss(primary.id)}
                        disabled={!!dismissing[primary.id]}
                      >
                        {t('learnerQuestions.dismissAll')}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ))}
    </div>
  );
};

export default CreatorLearnerQuestionsPage;
